import { produce } from "immer";
import {
  FETCH_AI_CONVERSATIONS,
  FETCH_AI_CONVERSATIONS_SUCCESS,
  FETCH_AI_CONVERSATIONS_FAILURE,
  FETCH_AI_CONVERSATION,
  FETCH_AI_CONVERSATION_SUCCESS,
  FETCH_AI_CONVERSATION_FAILURE,
  CREATE_AI_CONVERSATION,
  CREATE_AI_CONVERSATION_SUCCESS,
  CREATE_AI_CONVERSATION_FAILURE,
  SEND_AI_MESSAGE,
  SEND_AI_MESSAGE_SUCCESS,
  SEND_AI_MESSAGE_FAILURE,
  APPEND_OPTIMISTIC_AI_MESSAGE,
  RESET_AI_CONVERSATION_STATE,
  FETCH_AI_LOGS,
  FETCH_AI_LOGS_SUCCESS,
  FETCH_AI_LOGS_FAILURE,
} from "./constants";

const initialState = {
  conversations: [],
  activeUuid: null,
  activeResidentId: null,
  messages: [],
  // AI Logs review: holds the read-only list of conversations + their
  // messages fetched together via FETCH_AI_LOGS.
  activeConversations: [],
  // {uuid: template_name} map covering all active templates in the
  // conversation's location, so we can resolve any UUID the AI mentions.
  logsTemplatesByUuid: {},
  isFetchingList: false,
  isFetchingConversation: false,
  isFetchingLogs: false,
  isCreating: false,
  isSending: false,
  error: null,
};

const AiConversationsReducer = produce((draft, action) => {
  switch (action.type) {
    case FETCH_AI_CONVERSATIONS:
      draft.isFetchingList = true;
      draft.error = null;
      break;
    case FETCH_AI_CONVERSATIONS_SUCCESS:
      draft.isFetchingList = false;
      draft.conversations = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.data || [];
      break;
    case FETCH_AI_CONVERSATIONS_FAILURE:
      draft.isFetchingList = false;
      draft.error = action.payload || "Failed to load conversations";
      break;

    case FETCH_AI_CONVERSATION:
      draft.isFetchingConversation = true;
      draft.error = null;
      break;
    case FETCH_AI_CONVERSATION_SUCCESS: {
      draft.isFetchingConversation = false;
      const c = action.payload?.data ?? action.payload;
      if (c && c.uuid) {
        draft.activeUuid = c.uuid;
        draft.activeResidentId = c.resident_id;
        draft.messages = c.messages || [];
      }
      break;
    }
    case FETCH_AI_CONVERSATION_FAILURE:
      draft.isFetchingConversation = false;
      draft.error = action.payload || "Failed to load conversation";
      break;

    case CREATE_AI_CONVERSATION:
      draft.isCreating = true;
      draft.error = null;
      break;
    case CREATE_AI_CONVERSATION_SUCCESS: {
      draft.isCreating = false;
      const c = action.payload?.data ?? action.payload;
      if (c && c.uuid) {
        draft.activeUuid = c.uuid;
        draft.activeResidentId = c.resident_id;
        draft.messages = [];
        draft.conversations = [c, ...draft.conversations];
      }
      break;
    }
    case CREATE_AI_CONVERSATION_FAILURE:
      draft.isCreating = false;
      draft.error = action.payload || "Failed to create conversation";
      break;

    case APPEND_OPTIMISTIC_AI_MESSAGE:
      draft.messages.push({
        uuid: `optimistic-${Date.now()}`,
        role: action.payload?.role || "user",
        content: action.payload?.content || "",
        created_at: new Date().toISOString(),
        optimistic: true,
      });
      break;

    case SEND_AI_MESSAGE:
      draft.isSending = true;
      draft.error = null;
      break;
    case SEND_AI_MESSAGE_SUCCESS: {
      draft.isSending = false;
      const payload = action.payload?.data ?? action.payload;
      const msg = payload?.assistant_message;
      if (msg) {
        draft.messages.push(msg);
      }
      break;
    }
    case SEND_AI_MESSAGE_FAILURE:
      draft.isSending = false;
      draft.error = action.payload || "Failed to send message";
      break;

    case FETCH_AI_LOGS:
      draft.isFetchingLogs = true;
      draft.error = null;
      break;
    case FETCH_AI_LOGS_SUCCESS: {
      draft.isFetchingLogs = false;
      const payload = action.payload?.data ?? action.payload;
      draft.activeConversations = Array.isArray(payload?.conversations)
        ? payload.conversations
        : [];
      draft.logsTemplatesByUuid =
        payload?.templatesByUuid && typeof payload.templatesByUuid === "object"
          ? payload.templatesByUuid
          : {};
      break;
    }
    case FETCH_AI_LOGS_FAILURE:
      draft.isFetchingLogs = false;
      draft.error = action.payload || "Failed to load AI logs";
      break;

    case RESET_AI_CONVERSATION_STATE:
      return initialState;

    default:
      break;
  }
}, initialState);

export default AiConversationsReducer;
